import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "../general/HttpClient";
import { Button, Modal, FormGroup, Input, Label } from "../components/Modal";

// #region Types

interface TemporaryAccessConfigDetails {
    emailTemplate: string;
    emailDomain: string;
    displayNameTemplate: string;
    firstNameTemplate: string;
    lastNameTemplate: string;
    jobTitle: string;
    department: string;
    state: string;
    country: string;
    postalCode: string;
    usageLocation: string;
    licenseSkuIds: string[];
    securityGroupIds: string[];
    passwordType: number; // 0 = Strong, 1 = EasyToRemember, 2 = PrefixPlusFourDigits
    passwordLength: number | null;
    passwordPrefix: string;
    startNumber: number;
    accountCount: number;
}

interface TemporaryAccessConfigViewModel {
    id: string;
    organizationID: string;
    name: string;
    description: string;
    details: TemporaryAccessConfigDetails;
    enabled: boolean;
    createdAt: string;
    updatedAt: string | null;
    createdByUserID: string;
    createdByUserName: string;
    totalAccounts: number;
    enabledAccounts: number;
    disabledAccounts: number;
    accountsWithLicenses: number;
}

interface TemporaryAccountViewModel {
    id: string;
    configID: string;
    localUserID: string;
    azureUserID: string;
    userPrincipalName: string;
    displayName: string;
    sequenceNumber: number;
    accountEnabled: boolean;
    licensesAssigned: boolean;
    securityGroupsAssigned: boolean;
    createdAt: string;
    lastPasswordResetAt: string | null;
    disabledAt: string | null;
    errorMessage: string;
}

interface ProvisionAccountsResult {
    success: boolean;
    errorMessage: string;
    totalRequested: number;
    successfullyCreated: number;
    failed: number;
    errors: { sequenceNumber: number; userPrincipalName: string; errorMessage: string }[];
    createdAccounts: TemporaryAccountViewModel[];
}

interface PasswordResetResult {
    success: boolean;
    errorMessage: string;
    accountId: string;
    userPrincipalName: string;
    newPassword: string;
}

interface BulkPasswordResetResult {
    success: boolean;
    errorMessage: string;
    totalRequested: number;
    successfullyReset: number;
    failed: number;
    results: PasswordResetResult[];
}

interface BulkEnableDisableResult {
    success: boolean;
    errorMessage: string;
    totalRequested: number;
    successfullyProcessed: number;
    failed: number;
    results: { success: boolean; errorMessage: string; accountId: string; userPrincipalName: string; enabled: boolean }[];
}

interface SubscribedSku {
    id: string;
    skuId: string;
    skuPartNumber: string;
    consumedUnits: number;
    prepaidUnitsEnabled: number;
}

interface SubscribedSkusResult {
    success: boolean;
    errorMessage?: string;
    skus: SubscribedSku[];
}

interface AzureSecurityGroupInfo {
    id: string;
    displayName: string;
    description: string;
    mailNickname: string;
    securityEnabled: boolean;
    memberCount: number | null;
}

interface AzureSecurityGroupsResult {
    success: boolean;
    errorMessage?: string;
    groups: AzureSecurityGroupInfo[];
}

interface TemporaryAccountExportViewModel {
    userPrincipalName: string;
    displayName: string;
    password: string;
    sequenceNumber: number;
    accountEnabled: boolean;
}

interface ConfigDriftItem {
    type: "info" | "warning" | "error";
    label: string;
    description: string;
}

// #endregion

interface TemporaryAccessIndexProps {
    organizationId: string;
}

type View = "list" | "create" | "edit" | "detail";

const TemporaryAccessIndex: React.FC<TemporaryAccessIndexProps> = ({ organizationId }) => {
    // View state
    const [view, setView] = React.useState<View>("list");
    const [selectedConfig, setSelectedConfig] = React.useState<TemporaryAccessConfigViewModel | null>(null);

    // Config list
    const [configs, setConfigs] = React.useState<TemporaryAccessConfigViewModel[]>([]);
    const [loadingConfigs, setLoadingConfigs] = React.useState(false);

    // Accounts
    const [accounts, setAccounts] = React.useState<TemporaryAccountViewModel[]>([]);
    const [loadingAccounts, setLoadingAccounts] = React.useState(false);
    const [selectedAccountIds, setSelectedAccountIds] = React.useState<Set<string>>(new Set());

    // Reference data
    const [availableSkus, setAvailableSkus] = React.useState<SubscribedSku[]>([]);
    const [availableGroups, setAvailableGroups] = React.useState<AzureSecurityGroupInfo[]>([]);
    const [loadingRefData, setLoadingRefData] = React.useState(false);

    // Action states
    const [actionLoading, setActionLoading] = React.useState(false);
    const [actionMessage, setActionMessage] = React.useState<{ type: "success" | "error"; text: string } | null>(null);

    // Modals
    const [showProvisionModal, setShowProvisionModal] = React.useState(false);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [showBulkResetModal, setShowBulkResetModal] = React.useState(false);
    const [showBulkEnableModal, setShowBulkEnableModal] = React.useState(false);
    const [bulkEnableAction, setBulkEnableAction] = React.useState(true); // true = enable, false = disable
    const [showExportModal, setShowExportModal] = React.useState(false);
    const [showResetResultModal, setShowResetResultModal] = React.useState(false);

    // Provision
    const [provisionResult, setProvisionResult] = React.useState<ProvisionAccountsResult | null>(null);

    // Reset results
    const [resetResults, setResetResults] = React.useState<PasswordResetResult[]>([]);

    // Create/Edit form
    const [formName, setFormName] = React.useState("");
    const [formDescription, setFormDescription] = React.useState("");
    const [formEmailTemplate, setFormEmailTemplate] = React.useState("");
    const [formEmailDomain, setFormEmailDomain] = React.useState("");
    const [formDisplayNameTemplate, setFormDisplayNameTemplate] = React.useState("Exam User {number}");
    const [formFirstNameTemplate, setFormFirstNameTemplate] = React.useState("Exam");
    const [formLastNameTemplate, setFormLastNameTemplate] = React.useState("User {number}");
    const [formJobTitle, setFormJobTitle] = React.useState("");
    const [formDepartment, setFormDepartment] = React.useState("");
    const [formState, setFormState] = React.useState("");
    const [formCountry, setFormCountry] = React.useState("");
    const [formPostalCode, setFormPostalCode] = React.useState("");
    const [formUsageLocation, setFormUsageLocation] = React.useState("");
    const [formLicenseSkuIds, setFormLicenseSkuIds] = React.useState<string[]>([]);
    const [formSecurityGroupIds, setFormSecurityGroupIds] = React.useState<string[]>([]);
    const [formPasswordType, setFormPasswordType] = React.useState(0);
    const [formPasswordLength, setFormPasswordLength] = React.useState<number | null>(null);
    const [formPasswordPrefix, setFormPasswordPrefix] = React.useState("");
    const [formStartNumber, setFormStartNumber] = React.useState(1);
    const [formAccountCount, setFormAccountCount] = React.useState(10);
    const [formEnabled, setFormEnabled] = React.useState(true);

    // #region API Calls

    const loadConfigs = async () => {
        setLoadingConfigs(true);
        try {
            const response = await axios.get<TemporaryAccessConfigViewModel[]>(`/TemporaryAccess/Config/Organization/${organizationId}`);
            setConfigs(response.data);
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || "Failed to load configurations" });
        } finally {
            setLoadingConfigs(false);
        }
    };

    const loadAccounts = async (configId: string) => {
        setLoadingAccounts(true);
        try {
            const response = await axios.get<TemporaryAccountViewModel[]>(`/TemporaryAccess/Config/${configId}/Accounts`);
            setAccounts(response.data);
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || "Failed to load accounts" });
        } finally {
            setLoadingAccounts(false);
        }
    };

    const loadReferenceData = async () => {
        setLoadingRefData(true);
        try {
            const [skusRes, groupsRes] = await Promise.all([
                axios.get<SubscribedSkusResult>(`/TemporaryAccess/Organization/${organizationId}/Licenses`),
                axios.get<AzureSecurityGroupsResult>(`/TemporaryAccess/Organization/${organizationId}/SecurityGroups`),
            ]);
            if (skusRes.data.success) setAvailableSkus(skusRes.data.skus || []);
            if (groupsRes.data.success) setAvailableGroups(groupsRes.data.groups || []);
        } catch (error: any) {
            console.error("Failed to load reference data", error);
        } finally {
            setLoadingRefData(false);
        }
    };

    const createConfig = async () => {
        setActionLoading(true);
        setActionMessage(null);
        try {
            const payload = {
                name: formName,
                description: formDescription,
                details: {
                    emailTemplate: formEmailTemplate,
                    emailDomain: formEmailDomain,
                    displayNameTemplate: formDisplayNameTemplate,
                    firstNameTemplate: formFirstNameTemplate,
                    lastNameTemplate: formLastNameTemplate,
                    jobTitle: formJobTitle,
                    department: formDepartment,
                    state: formState,
                    country: formCountry,
                    postalCode: formPostalCode,
                    usageLocation: formUsageLocation,
                    licenseSkuIds: formLicenseSkuIds,
                    securityGroupIds: formSecurityGroupIds,
                    passwordType: formPasswordType,
                    passwordLength: formPasswordLength,
                    passwordPrefix: formPasswordPrefix,
                    startNumber: formStartNumber,
                    accountCount: formAccountCount,
                },
            };
            await axios.post(`/TemporaryAccess/Config/Organization/${organizationId}`, payload);
            setActionMessage({ type: "success", text: "Configuration created successfully" });
            setView("list");
            loadConfigs();
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || "Failed to create configuration" });
        } finally {
            setActionLoading(false);
        }
    };

    const updateConfig = async () => {
        if (!selectedConfig) return;
        setActionLoading(true);
        setActionMessage(null);
        try {
            const payload = {
                name: formName,
                description: formDescription,
                details: {
                    emailTemplate: formEmailTemplate,
                    emailDomain: formEmailDomain,
                    displayNameTemplate: formDisplayNameTemplate,
                    firstNameTemplate: formFirstNameTemplate,
                    lastNameTemplate: formLastNameTemplate,
                    jobTitle: formJobTitle,
                    department: formDepartment,
                    state: formState,
                    country: formCountry,
                    postalCode: formPostalCode,
                    usageLocation: formUsageLocation,
                    licenseSkuIds: formLicenseSkuIds,
                    securityGroupIds: formSecurityGroupIds,
                    passwordType: formPasswordType,
                    passwordLength: formPasswordLength,
                    passwordPrefix: formPasswordPrefix,
                    startNumber: formStartNumber,
                    accountCount: formAccountCount,
                },
                enabled: formEnabled,
            };
            const response = await axios.put<TemporaryAccessConfigViewModel>(`/TemporaryAccess/Config/${selectedConfig.id}`, payload);
            setSelectedConfig(response.data);
            setActionMessage({ type: "success", text: "Configuration updated successfully" });
            setView("detail");
            loadConfigs();
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || "Failed to update configuration" });
        } finally {
            setActionLoading(false);
        }
    };

    const deleteConfig = async () => {
        if (!selectedConfig) return;
        setActionLoading(true);
        setActionMessage(null);
        try {
            await axios.delete(`/TemporaryAccess/Config/${selectedConfig.id}`);
            setActionMessage({ type: "success", text: "Configuration deleted successfully" });
            setShowDeleteModal(false);
            setView("list");
            setSelectedConfig(null);
            loadConfigs();
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || "Failed to delete configuration" });
        } finally {
            setActionLoading(false);
        }
    };

    const provisionAccounts = async () => {
        if (!selectedConfig) return;
        setActionLoading(true);
        setActionMessage(null);
        setProvisionResult(null);
        try {
            const response = await axios.post<ProvisionAccountsResult>(`/TemporaryAccess/Config/${selectedConfig.id}/Provision`, {});
            setProvisionResult(response.data);
            if (response.data.success) {
                setActionMessage({ type: "success", text: `Successfully provisioned ${response.data.successfullyCreated} accounts` });
            } else {
                setActionMessage({
                    type: "error",
                    text: response.data.errorMessage || `${response.data.failed} accounts failed to provision`,
                });
            }
            setShowProvisionModal(false);
            loadAccounts(selectedConfig.id);
            // refresh config to update stats
            const configRes = await axios.get<TemporaryAccessConfigViewModel>(`/TemporaryAccess/Config/${selectedConfig.id}`);
            setSelectedConfig(configRes.data);
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || error.response?.data?.errorMessage || "Failed to provision accounts" });
        } finally {
            setActionLoading(false);
        }
    };

    const resetSinglePassword = async (accountId: string) => {
        setActionLoading(true);
        setActionMessage(null);
        try {
            const response = await axios.post<PasswordResetResult>(`/TemporaryAccess/Account/${accountId}/ResetPassword`);
            if (response.data.success) {
                setResetResults([response.data]);
                setShowResetResultModal(true);
                setActionMessage({ type: "success", text: `Password reset for ${response.data.userPrincipalName}` });
            } else {
                setActionMessage({ type: "error", text: response.data.errorMessage || "Failed to reset password" });
            }
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || "Failed to reset password" });
        } finally {
            setActionLoading(false);
        }
    };

    const bulkResetPasswords = async () => {
        if (!selectedConfig) return;
        setActionLoading(true);
        setActionMessage(null);
        try {
            const payload = {
                accountIds: selectedAccountIds.size > 0 ? Array.from(selectedAccountIds) : [],
                resetAll: selectedAccountIds.size === 0,
            };
            const response = await axios.post<BulkPasswordResetResult>(`/TemporaryAccess/Config/${selectedConfig.id}/BulkResetPasswords`, payload);
            setShowBulkResetModal(false);
            if (response.data.success) {
                setResetResults(response.data.results);
                setShowResetResultModal(true);
                setActionMessage({ type: "success", text: `Successfully reset ${response.data.successfullyReset} passwords` });
            } else {
                setActionMessage({ type: "error", text: response.data.errorMessage || `${response.data.failed} password resets failed` });
            }
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || "Failed to bulk reset passwords" });
        } finally {
            setActionLoading(false);
        }
    };

    const toggleSingleAccount = async (accountId: string, enable: boolean) => {
        setActionLoading(true);
        setActionMessage(null);
        try {
            await axios.post(`/TemporaryAccess/Account/${accountId}/SetEnabled?enable=${enable}`);
            setActionMessage({ type: "success", text: `Account ${enable ? "enabled" : "disabled"} successfully` });
            if (selectedConfig) {
                loadAccounts(selectedConfig.id);
                const configRes = await axios.get<TemporaryAccessConfigViewModel>(`/TemporaryAccess/Config/${selectedConfig.id}`);
                setSelectedConfig(configRes.data);
            }
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || `Failed to ${enable ? "enable" : "disable"} account` });
        } finally {
            setActionLoading(false);
        }
    };

    const bulkEnableDisable = async () => {
        if (!selectedConfig) return;
        setActionLoading(true);
        setActionMessage(null);
        try {
            const payload = {
                accountIds: selectedAccountIds.size > 0 ? Array.from(selectedAccountIds) : [],
                applyToAll: selectedAccountIds.size === 0,
                enable: bulkEnableAction,
            };
            const response = await axios.post<BulkEnableDisableResult>(`/TemporaryAccess/Config/${selectedConfig.id}/BulkSetEnabled`, payload);
            setShowBulkEnableModal(false);
            if (response.data.success) {
                setActionMessage({
                    type: "success",
                    text: `Successfully ${bulkEnableAction ? "enabled" : "disabled"} ${response.data.successfullyProcessed} accounts`,
                });
            } else {
                setActionMessage({
                    type: "error",
                    text: response.data.errorMessage || `${response.data.failed} accounts failed`,
                });
            }
            loadAccounts(selectedConfig.id);
            const configRes = await axios.get<TemporaryAccessConfigViewModel>(`/TemporaryAccess/Config/${selectedConfig.id}`);
            setSelectedConfig(configRes.data);
            setSelectedAccountIds(new Set());
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || "Failed to update accounts" });
        } finally {
            setActionLoading(false);
        }
    };

    const exportAccounts = async (format: "csv" | "json") => {
        if (!selectedConfig) return;
        try {
            if (format === "csv") {
                const response = await axios.get(`/TemporaryAccess/Config/${selectedConfig.id}/Export?format=csv`, {
                    responseType: "blob",
                });
                const url = URL.createObjectURL(new Blob([response.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = `${selectedConfig.name}-accounts.csv`;
                a.click();
                URL.revokeObjectURL(url);
            } else {
                const response = await axios.get<TemporaryAccountExportViewModel[]>(`/TemporaryAccess/Config/${selectedConfig.id}/Export?format=json`);
                const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${selectedConfig.name}-accounts.json`;
                a.click();
                URL.revokeObjectURL(url);
            }
            setShowExportModal(false);
            setActionMessage({ type: "success", text: `Accounts exported as ${format.toUpperCase()}` });
        } catch (error: any) {
            setActionMessage({ type: "error", text: error.response?.data?.error || "Failed to export accounts" });
        }
    };

    // #endregion

    // #region Effects

    React.useEffect(() => {
        if (organizationId) {
            loadConfigs();
        }
    }, [organizationId]);

    React.useEffect(() => {
        if ((view === "create" || view === "edit") && availableSkus.length === 0 && availableGroups.length === 0) {
            loadReferenceData();
        }
    }, [view]);

    // #endregion

    // #region Helpers

    const resetForm = () => {
        setFormName("");
        setFormDescription("");
        setFormEmailTemplate("");
        setFormEmailDomain("");
        setFormDisplayNameTemplate("Exam User {number}");
        setFormFirstNameTemplate("Exam");
        setFormLastNameTemplate("User {number}");
        setFormJobTitle("");
        setFormDepartment("");
        setFormState("");
        setFormCountry("");
        setFormPostalCode("");
        setFormUsageLocation("");
        setFormLicenseSkuIds([]);
        setFormSecurityGroupIds([]);
        setFormPasswordType(0);
        setFormPasswordLength(null);
        setFormPasswordPrefix("");
        setFormStartNumber(1);
        setFormAccountCount(10);
        setFormEnabled(true);
    };

    const openCreateView = () => {
        resetForm();
        setActionMessage(null);
        setView("create");
    };

    const openDetail = (config: TemporaryAccessConfigViewModel) => {
        setSelectedConfig(config);
        setSelectedAccountIds(new Set());
        setActionMessage(null);
        setProvisionResult(null);
        setView("detail");
        loadAccounts(config.id);
    };

    const backToList = () => {
        setView("list");
        setSelectedConfig(null);
        setAccounts([]);
        setSelectedAccountIds(new Set());
        setActionMessage(null);
    };

    const backToDetail = () => {
        setView("detail");
        setActionMessage(null);
    };

    const populateFormFromConfig = (config: TemporaryAccessConfigViewModel) => {
        setFormName(config.name);
        setFormDescription(config.description || "");
        setFormEmailTemplate(config.details.emailTemplate || "");
        setFormEmailDomain(config.details.emailDomain || "");
        setFormDisplayNameTemplate(config.details.displayNameTemplate || "Exam User {number}");
        setFormFirstNameTemplate(config.details.firstNameTemplate || "Exam");
        setFormLastNameTemplate(config.details.lastNameTemplate || "User {number}");
        setFormJobTitle(config.details.jobTitle || "");
        setFormDepartment(config.details.department || "");
        setFormState(config.details.state || "");
        setFormCountry(config.details.country || "");
        setFormPostalCode(config.details.postalCode || "");
        setFormUsageLocation(config.details.usageLocation || "");
        setFormLicenseSkuIds(config.details.licenseSkuIds || []);
        setFormSecurityGroupIds(config.details.securityGroupIds || []);
        setFormPasswordType(config.details.passwordType ?? 0);
        setFormPasswordLength(config.details.passwordLength);
        setFormPasswordPrefix(config.details.passwordPrefix || "");
        setFormStartNumber(config.details.startNumber ?? 1);
        setFormAccountCount(config.details.accountCount ?? 10);
        setFormEnabled(config.enabled);
    };

    const openEditView = () => {
        if (selectedConfig) {
            populateFormFromConfig(selectedConfig);
            setActionMessage(null);
            setView("edit");
        }
    };

    const toggleAccountSelect = (accountId: string) => {
        setSelectedAccountIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(accountId)) {
                newSet.delete(accountId);
            } else {
                newSet.add(accountId);
            }
            return newSet;
        });
    };

    const toggleSelectAll = () => {
        if (selectedAccountIds.size === accounts.length) {
            setSelectedAccountIds(new Set());
        } else {
            setSelectedAccountIds(new Set(accounts.map((a) => a.id)));
        }
    };

    const getSkuName = (skuId: string) => {
        const sku = availableSkus.find((s) => s.skuId === skuId);
        return sku?.skuPartNumber || skuId;
    };

    const getGroupName = (groupId: string) => {
        const group = availableGroups.find((g) => g.id === groupId);
        return group?.displayName || groupId;
    };

    const toggleLicenseSku = (skuId: string) => {
        setFormLicenseSkuIds((prev) => (prev.includes(skuId) ? prev.filter((id) => id !== skuId) : [...prev, skuId]));
    };

    const toggleSecurityGroup = (groupId: string) => {
        setFormSecurityGroupIds((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
    };

    const isValidEmailTemplate = (template: string): boolean => {
        if (!template) return false;
        return /\{number(:\s*pad\d+)?\}/i.test(template);
    };

    const computeConfigDrift = (): ConfigDriftItem[] => {
        if (!selectedConfig || accounts.length === 0) return [];
        const diffs: ConfigDriftItem[] = [];

        // Account count mismatch
        const expectedCount = selectedConfig.details.accountCount;
        const actualCount = accounts.length;
        if (actualCount !== expectedCount) {
            diffs.push({
                type: actualCount < expectedCount ? "warning" : "info",
                label: "Account Count Mismatch",
                description: `Config expects ${expectedCount} accounts, but ${actualCount} are provisioned.${
                    actualCount < expectedCount ? ` ${expectedCount - actualCount} account(s) still need to be created.` : ` ${actualCount - expectedCount} extra account(s) beyond config count.`
                }`,
            });
        }

        // License assignment gaps
        if (selectedConfig.details.licenseSkuIds?.length > 0) {
            const unlicensed = accounts.filter((a) => !a.licensesAssigned);
            if (unlicensed.length > 0) {
                diffs.push({
                    type: "warning",
                    label: "License Assignment Gaps",
                    description: `${unlicensed.length} of ${actualCount} account(s) do not have licenses assigned (config requires ${selectedConfig.details.licenseSkuIds.length} license SKU(s)).`,
                });
            }
        }

        // Security group assignment gaps
        if (selectedConfig.details.securityGroupIds?.length > 0) {
            const ungrouped = accounts.filter((a) => !a.securityGroupsAssigned);
            if (ungrouped.length > 0) {
                diffs.push({
                    type: "warning",
                    label: "Security Group Gaps",
                    description: `${ungrouped.length} of ${actualCount} account(s) are not assigned to all configured security groups.`,
                });
            }
        }

        // Disabled accounts
        const disabledCount = accounts.filter((a) => !a.accountEnabled).length;
        if (disabledCount > 0) {
            diffs.push({
                type: "info",
                label: "Disabled Accounts",
                description: `${disabledCount} of ${actualCount} account(s) are currently disabled.`,
            });
        }

        // Accounts with errors
        const errorAccounts = accounts.filter((a) => a.errorMessage);
        if (errorAccounts.length > 0) {
            diffs.push({
                type: "error",
                label: "Accounts with Errors",
                description: `${errorAccounts.length} account(s) have errors: ${errorAccounts
                    .slice(0, 5)
                    .map((a) => a.userPrincipalName)
                    .join(", ")}${errorAccounts.length > 5 ? ` and ${errorAccounts.length - 5} more` : ""}`,
            });
        }

        // Email domain drift
        const configDomain = selectedConfig.details.emailDomain;
        if (configDomain) {
            const mismatchedDomain = accounts.filter((a) => a.userPrincipalName && !a.userPrincipalName.toLowerCase().endsWith(`@${configDomain.toLowerCase()}`));
            if (mismatchedDomain.length > 0) {
                diffs.push({
                    type: "warning",
                    label: "Email Domain Mismatch",
                    description: `${mismatchedDomain.length} account(s) have a different email domain than the current config (@${configDomain}).`,
                });
            }
        }

        return diffs;
    };

    // #endregion

    // #region Config List View

    const renderListView = () => (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900">
                    <FontAwesomeIcon icon={["fad", "users-cog"]} className="mr-2 text-blue-600" />
                    Temporary Access Configurations
                </h4>
                <Button variant="primary" size="sm" onClick={openCreateView}>
                    <FontAwesomeIcon icon={["fad", "plus"]} className="mr-1" />
                    New Configuration
                </Button>
            </div>

            {loadingConfigs && (
                <div className="flex items-center justify-center py-8 text-gray-500">
                    <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                    Loading configurations...
                </div>
            )}

            {!loadingConfigs && configs.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                    <FontAwesomeIcon icon={["fad", "folder-open"]} className="w-12 h-12 mb-3 text-gray-300" />
                    <p className="text-lg font-medium mb-1">No configurations yet</p>
                    <p className="text-sm">Create a temporary access configuration to get started.</p>
                </div>
            )}

            {!loadingConfigs && configs.length > 0 && (
                <div className="space-y-3">
                    {configs.map((config) => (
                        <div key={config.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => openDetail(config)}>
                            <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-900">{config.name}</span>
                                        <span
                                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                config.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                                            }`}
                                        >
                                            {config.enabled ? "Active" : "Disabled"}
                                        </span>
                                    </div>
                                    {config.description && <p className="text-sm text-gray-500 mt-1 truncate">{config.description}</p>}
                                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                        <span>
                                            <FontAwesomeIcon icon={["fad", "users"]} className="mr-1 text-blue-500" />
                                            {config.totalAccounts} accounts
                                        </span>
                                        <span>
                                            <FontAwesomeIcon icon={["fad", "check-circle"]} className="mr-1 text-green-500" />
                                            {config.enabledAccounts} enabled
                                        </span>
                                        <span>
                                            <FontAwesomeIcon icon={["fad", "ban"]} className="mr-1 text-red-500" />
                                            {config.disabledAccounts} disabled
                                        </span>
                                        <span>
                                            <FontAwesomeIcon icon={["fad", "id-card"]} className="mr-1 text-purple-500" />
                                            {config.accountsWithLicenses} licensed
                                        </span>
                                    </div>
                                </div>
                                <FontAwesomeIcon icon={["fad", "chevron-right"]} className="text-gray-400" />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // #endregion

    // #region Create/Edit View

    const renderFormView = () => (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Button variant="secondary" size="sm" onClick={view === "edit" ? backToDetail : backToList}>
                    <FontAwesomeIcon icon={["fad", "arrow-left"]} className="mr-1" />
                    Back
                </Button>
                <h4 className="text-lg font-medium text-gray-900">
                    <FontAwesomeIcon icon={["fad", view === "edit" ? "edit" : "plus-circle"]} className="mr-2 text-blue-600" />
                    {view === "edit" ? `Edit Configuration: ${selectedConfig?.name}` : "New Temporary Access Configuration"}
                </h4>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
                {/* Basic Info */}
                <div>
                    <h5 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3">
                        <FontAwesomeIcon icon={["fad", "info-circle"]} className="mr-1 text-blue-500" />
                        Basic Information
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormGroup>
                            <Label required>Configuration Name</Label>
                            <Input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g. 2025 June Exams" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Description</Label>
                            <Input type="text" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder="Optional description" />
                        </FormGroup>
                    </div>
                </div>

                {/* Email & Naming Templates */}
                <div>
                    <h5 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3">
                        <FontAwesomeIcon icon={["fad", "envelope"]} className="mr-1 text-blue-500" />
                        Email & Naming Templates
                    </h5>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
                        <FontAwesomeIcon icon={["fad", "lightbulb"]} className="mr-1" />
                        <strong>Placeholders:</strong> Use <code className="bg-blue-100 px-1 rounded">{"{number}"}</code> for sequential number,{" "}
                        <code className="bg-blue-100 px-1 rounded">{"{number:padN}"}</code> for zero-padded (e.g. pad3 = 001), and <code className="bg-blue-100 px-1 rounded">{"{orgprefix}"}</code> for
                        organization prefix.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormGroup>
                            <Label required>Email Template</Label>
                            <Input
                                type="text"
                                value={formEmailTemplate}
                                onChange={(e) => setFormEmailTemplate(e.target.value)}
                                placeholder="e.g. EE{number:pad3} or {orgprefix}exam{number}"
                                className={formEmailTemplate && !isValidEmailTemplate(formEmailTemplate) ? "border-red-400" : ""}
                            />
                            {formEmailTemplate && !isValidEmailTemplate(formEmailTemplate) && (
                                <small className="text-red-600 mt-1">
                                    <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="mr-1" />
                                    Template must contain <code className="bg-red-50 px-1 rounded">{"\{number\}"}</code> or <code className="bg-red-50 px-1 rounded">{"\{number:padN\}"}</code> to
                                    generate unique accounts.
                                </small>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label required>Email Domain</Label>
                            <Input type="text" value={formEmailDomain} onChange={(e) => setFormEmailDomain(e.target.value)} placeholder="e.g. contoso.onmicrosoft.com" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Display Name Template</Label>
                            <Input type="text" value={formDisplayNameTemplate} onChange={(e) => setFormDisplayNameTemplate(e.target.value)} placeholder="Exam User {number}" />
                        </FormGroup>
                        <FormGroup>
                            <Label>First Name Template</Label>
                            <Input type="text" value={formFirstNameTemplate} onChange={(e) => setFormFirstNameTemplate(e.target.value)} placeholder="Exam" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Last Name Template</Label>
                            <Input type="text" value={formLastNameTemplate} onChange={(e) => setFormLastNameTemplate(e.target.value)} placeholder="User {number}" />
                        </FormGroup>
                    </div>
                </div>

                {/* Azure User Properties */}
                <div>
                    <h5 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3">
                        <FontAwesomeIcon icon={["fad", "building"]} className="mr-1 text-blue-500" />
                        Azure User Properties
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormGroup>
                            <Label>Job Title</Label>
                            <Input type="text" value={formJobTitle} onChange={(e) => setFormJobTitle(e.target.value)} placeholder="e.g. Exam Candidate" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Department</Label>
                            <Input type="text" value={formDepartment} onChange={(e) => setFormDepartment(e.target.value)} placeholder="e.g. Examinations" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Usage Location</Label>
                            <Input type="text" value={formUsageLocation} onChange={(e) => setFormUsageLocation(e.target.value)} placeholder="e.g. ZA" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Country</Label>
                            <Input type="text" value={formCountry} onChange={(e) => setFormCountry(e.target.value)} placeholder="e.g. South Africa" />
                        </FormGroup>
                        <FormGroup>
                            <Label>State</Label>
                            <Input type="text" value={formState} onChange={(e) => setFormState(e.target.value)} placeholder="e.g. Gauteng" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Postal Code</Label>
                            <Input type="text" value={formPostalCode} onChange={(e) => setFormPostalCode(e.target.value)} placeholder="e.g. 2000" />
                        </FormGroup>
                    </div>
                </div>

                {/* Licenses */}
                <div>
                    <h5 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3">
                        <FontAwesomeIcon icon={["fad", "id-card"]} className="mr-1 text-purple-500" />
                        License Assignment
                    </h5>
                    {loadingRefData ? (
                        <div className="flex items-center text-gray-500 text-sm">
                            <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                            Loading available licenses...
                        </div>
                    ) : availableSkus.length === 0 ? (
                        <p className="text-sm text-gray-500">No licenses available for this organization.</p>
                    ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                            {availableSkus.map((sku) => {
                                const available = sku.prepaidUnitsEnabled - sku.consumedUnits;
                                return (
                                    <label
                                        key={sku.skuId}
                                        className={`flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer ${
                                            formLicenseSkuIds.includes(sku.skuId) ? "bg-purple-50 border border-purple-200" : ""
                                        }`}
                                    >
                                        <Input type="checkbox" checked={formLicenseSkuIds.includes(sku.skuId)} onChange={() => toggleLicenseSku(sku.skuId)} />
                                        <div className="flex-1">
                                            <span className="text-sm font-medium text-gray-900">{sku.skuPartNumber}</span>
                                            <span className={`ml-2 text-xs ${available > 0 ? "text-green-600" : "text-red-600"}`}>{available} available</span>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Security Groups */}
                <div>
                    <h5 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3">
                        <FontAwesomeIcon icon={["fad", "shield-alt"]} className="mr-1 text-green-500" />
                        Security Groups
                    </h5>
                    {loadingRefData ? (
                        <div className="flex items-center text-gray-500 text-sm">
                            <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                            Loading security groups...
                        </div>
                    ) : availableGroups.length === 0 ? (
                        <p className="text-sm text-gray-500">No security groups available for this organization.</p>
                    ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                            {availableGroups.map((group) => (
                                <label
                                    key={group.id}
                                    className={`flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer ${
                                        formSecurityGroupIds.includes(group.id) ? "bg-green-50 border border-green-200" : ""
                                    }`}
                                >
                                    <Input type="checkbox" checked={formSecurityGroupIds.includes(group.id)} onChange={() => toggleSecurityGroup(group.id)} />
                                    <div className="flex-1">
                                        <span className="text-sm font-medium text-gray-900">{group.displayName}</span>
                                        {group.description && <span className="ml-2 text-xs text-gray-500">{group.description}</span>}
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* Password & Account Settings */}
                <div>
                    <h5 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3">
                        <FontAwesomeIcon icon={["fad", "key"]} className="mr-1 text-yellow-500" />
                        Password & Account Settings
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <FormGroup>
                            <Label>Password Type</Label>
                            <Input type="select" value={formPasswordType} onChange={(e) => setFormPasswordType(Number(e.target.value))}>
                                <option value={0}>Strong</option>
                                <option value={1}>Easy to Remember</option>
                                <option value={2}>Prefix + 4 Digits</option>
                            </Input>
                        </FormGroup>
                        {formPasswordType === 2 && (
                            <FormGroup>
                                <Label required>Password Prefix</Label>
                                <Input type="text" value={formPasswordPrefix} onChange={(e) => setFormPasswordPrefix(e.target.value)} placeholder="e.g. Exam" />
                                <small className="text-gray-500">Generates passwords like {formPasswordPrefix || "Exam"}2345</small>
                            </FormGroup>
                        )}
                        {formPasswordType === 0 && (
                            <FormGroup>
                                <Label>Password Length</Label>
                                <Input
                                    type="number"
                                    value={formPasswordLength ?? ""}
                                    onChange={(e) => setFormPasswordLength(e.target.value ? Number(e.target.value) : null)}
                                    placeholder="Default"
                                    min={8}
                                    max={64}
                                />
                            </FormGroup>
                        )}
                        <FormGroup>
                            <Label required>Start Number</Label>
                            <Input type="number" value={formStartNumber} onChange={(e) => setFormStartNumber(Number(e.target.value))} min={1} />
                        </FormGroup>
                        <FormGroup>
                            <Label required>Account Count</Label>
                            <Input type="number" value={formAccountCount} onChange={(e) => setFormAccountCount(Number(e.target.value))} min={1} max={500} />
                        </FormGroup>
                    </div>
                </div>

                {/* Enabled Toggle (edit only) */}
                {view === "edit" && (
                    <div>
                        <h5 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3">
                            <FontAwesomeIcon icon={["fad", "toggle-on"]} className="mr-1 text-blue-500" />
                            Status
                        </h5>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <Input type="checkbox" checked={formEnabled} onChange={(e) => setFormEnabled((e.target as HTMLInputElement).checked)} />
                            <span className="text-sm font-medium text-gray-900">Configuration {formEnabled ? "Enabled" : "Disabled"}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${formEnabled ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                {formEnabled ? "Active" : "Inactive"}
                            </span>
                        </label>
                    </div>
                )}

                {/* Actions */}
                <div className="border-t border-gray-200 pt-4 flex justify-end space-x-3">
                    <Button variant="secondary" onClick={view === "edit" ? backToDetail : backToList} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={view === "edit" ? updateConfig : createConfig}
                        disabled={actionLoading || !formName || !formEmailTemplate || !formEmailDomain || formAccountCount < 1 || !isValidEmailTemplate(formEmailTemplate)}
                    >
                        {actionLoading ? (
                            <>
                                <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                                {view === "edit" ? "Saving..." : "Creating..."}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={["fad", "save"]} className="mr-2" />
                                {view === "edit" ? "Save Changes" : "Create Configuration"}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );

    // #endregion

    // #region Detail View

    const renderDetailView = () => {
        if (!selectedConfig) return null;

        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button variant="secondary" size="sm" onClick={backToList}>
                            <FontAwesomeIcon icon={["fad", "arrow-left"]} className="mr-1" />
                            Back
                        </Button>
                        <div>
                            <h4 className="text-lg font-medium text-gray-900">{selectedConfig.name}</h4>
                            {selectedConfig.description && <p className="text-sm text-gray-500">{selectedConfig.description}</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline-primary" size="sm" onClick={openEditView}>
                            <FontAwesomeIcon icon={["fad", "edit"]} className="mr-1" />
                            Edit
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => setShowDeleteModal(true)}>
                            <FontAwesomeIcon icon={["fad", "trash"]} className="mr-1" />
                            Delete
                        </Button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-blue-700">{selectedConfig.totalAccounts}</div>
                        <div className="text-sm text-blue-600">Total Accounts</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-green-700">{selectedConfig.enabledAccounts}</div>
                        <div className="text-sm text-green-600">Enabled</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-red-700">{selectedConfig.disabledAccounts}</div>
                        <div className="text-sm text-red-600">Disabled</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-purple-700">{selectedConfig.accountsWithLicenses}</div>
                        <div className="text-sm text-purple-600">Licensed</div>
                    </div>
                </div>

                {/* Actions Bar */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="primary" size="sm" onClick={() => setShowProvisionModal(true)}>
                            <FontAwesomeIcon icon={["fad", "user-plus"]} className="mr-1" />
                            Provision Accounts
                        </Button>

                        {selectedConfig.totalAccounts > 0 && (
                            <>
                                <Button variant="primary" size="sm" onClick={() => setShowExportModal(true)}>
                                    <FontAwesomeIcon icon={["fad", "file-export"]} className="mr-1" />
                                    Export
                                </Button>
                                <Button variant="warning" size="sm" onClick={() => setShowBulkResetModal(true)}>
                                    <FontAwesomeIcon icon={["fad", "key"]} className="mr-1" />
                                    {selectedAccountIds.size > 0 ? `Reset Passwords (${selectedAccountIds.size})` : "Reset All Passwords"}
                                </Button>
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => {
                                        setBulkEnableAction(true);
                                        setShowBulkEnableModal(true);
                                    }}
                                >
                                    <FontAwesomeIcon icon={["fad", "check-circle"]} className="mr-1" />
                                    {selectedAccountIds.size > 0 ? `Enable (${selectedAccountIds.size})` : "Enable All"}
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => {
                                        setBulkEnableAction(false);
                                        setShowBulkEnableModal(true);
                                    }}
                                >
                                    <FontAwesomeIcon icon={["fad", "ban"]} className="mr-1" />
                                    {selectedAccountIds.size > 0 ? `Disable (${selectedAccountIds.size})` : "Disable All"}
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Accounts Table */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                        <h5 className="text-sm font-medium text-gray-700">
                            <FontAwesomeIcon icon={["fad", "users"]} className="mr-1 text-blue-500" />
                            Accounts ({accounts.length})
                        </h5>
                        {accounts.length > 0 && (
                            <Button variant="outline-secondary" size="sm" onClick={toggleSelectAll}>
                                <FontAwesomeIcon icon={["fad", selectedAccountIds.size === accounts.length ? "check-square" : "square"]} className="mr-1" />
                                {selectedAccountIds.size === accounts.length ? "Deselect All" : "Select All"}
                            </Button>
                        )}
                    </div>

                    {loadingAccounts && (
                        <div className="flex items-center justify-center py-8 text-gray-500">
                            <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                            Loading accounts...
                        </div>
                    )}

                    {!loadingAccounts && accounts.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            <FontAwesomeIcon icon={["fad", "user-slash"]} className="w-10 h-10 mb-2 text-gray-300" />
                            <p>No accounts provisioned yet.</p>
                        </div>
                    )}

                    {!loadingAccounts && accounts.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                                            <Input type="checkbox" checked={selectedAccountIds.size === accounts.length && accounts.length > 0} onChange={toggleSelectAll} />
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Licenses</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Groups</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {accounts.map((account) => (
                                        <tr key={account.id} className={`hover:bg-gray-50 ${selectedAccountIds.has(account.id) ? "bg-blue-50" : ""}`}>
                                            <td className="px-4 py-3">
                                                <Input type="checkbox" checked={selectedAccountIds.has(account.id)} onChange={() => toggleAccountSelect(account.id)} />
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">{account.sequenceNumber}</td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm font-medium text-gray-900">{account.displayName}</div>
                                                <div className="text-xs text-gray-500">{account.userPrincipalName}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                        account.accountEnabled ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                                    }`}
                                                >
                                                    <FontAwesomeIcon icon={["fad", account.accountEnabled ? "check-circle" : "ban"]} className="mr-1" />
                                                    {account.accountEnabled ? "Enabled" : "Disabled"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                {account.licensesAssigned ? (
                                                    <FontAwesomeIcon icon={["fad", "check"]} className="text-green-500" title="Licenses assigned" />
                                                ) : (
                                                    <FontAwesomeIcon icon={["fad", "times"]} className="text-red-400" title="Licenses not assigned" />
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {account.securityGroupsAssigned ? (
                                                    <FontAwesomeIcon icon={["fad", "check"]} className="text-green-500" title="Groups assigned" />
                                                ) : (
                                                    <FontAwesomeIcon icon={["fad", "times"]} className="text-red-400" title="Groups not assigned" />
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-gray-500">{new Date(account.createdAt).toLocaleDateString()}</td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => resetSinglePassword(account.id)}
                                                        disabled={actionLoading}
                                                        className="p-1.5 rounded hover:bg-yellow-50 text-yellow-600 disabled:opacity-50"
                                                        title="Reset Password"
                                                    >
                                                        <FontAwesomeIcon icon={["fad", "key"]} className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => toggleSingleAccount(account.id, !account.accountEnabled)}
                                                        disabled={actionLoading}
                                                        className={`p-1.5 rounded ${account.accountEnabled ? "hover:bg-red-50 text-red-600" : "hover:bg-green-50 text-green-600"} disabled:opacity-50`}
                                                        title={account.accountEnabled ? "Disable Account" : "Enable Account"}
                                                    >
                                                        <FontAwesomeIcon icon={["fad", account.accountEnabled ? "ban" : "check-circle"]} className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Config vs Provisioned Drift */}
                {accounts.length > 0 &&
                    (() => {
                        const driftItems = computeConfigDrift();
                        if (driftItems.length === 0)
                            return (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-green-800">
                                        <FontAwesomeIcon icon={["fad", "check-circle"]} className="w-5 h-5" />
                                        <span className="font-medium">Configuration and provisioned accounts are in sync</span>
                                    </div>
                                </div>
                            );
                        return (
                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-200">
                                    <h5 className="text-sm font-medium text-gray-700">
                                        <FontAwesomeIcon icon={["fad", "exchange-alt"]} className="mr-1 text-orange-500" />
                                        Config vs Provisioned Differences ({driftItems.length})
                                    </h5>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {driftItems.map((item, i) => (
                                        <div key={i} className={`px-4 py-3 ${item.type === "error" ? "bg-red-50" : item.type === "warning" ? "bg-yellow-50" : "bg-blue-50"}`}>
                                            <div className="flex items-start gap-2">
                                                <FontAwesomeIcon
                                                    icon={["fad", item.type === "error" ? "exclamation-circle" : item.type === "warning" ? "exclamation-triangle" : "info-circle"]}
                                                    className={`mt-0.5 ${item.type === "error" ? "text-red-500" : item.type === "warning" ? "text-yellow-500" : "text-blue-500"}`}
                                                />
                                                <div>
                                                    <div className={`text-sm font-medium ${item.type === "error" ? "text-red-800" : item.type === "warning" ? "text-yellow-800" : "text-blue-800"}`}>
                                                        {item.label}
                                                    </div>
                                                    <div className={`text-xs mt-0.5 ${item.type === "error" ? "text-red-700" : item.type === "warning" ? "text-yellow-700" : "text-blue-700"}`}>
                                                        {item.description}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                {/* Config Details (collapsible) */}
                <details className="bg-white border border-gray-200 rounded-lg">
                    <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-50">
                        <FontAwesomeIcon icon={["fad", "cog"]} className="mr-1 text-gray-400" />
                        Configuration Details
                    </summary>
                    <div className="px-4 pb-4 pt-2 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                            <div>
                                <span className="font-medium text-gray-600">Email Template:</span> <span className="text-gray-900">{selectedConfig.details.emailTemplate}</span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600">Email Domain:</span> <span className="text-gray-900">{selectedConfig.details.emailDomain}</span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600">Display Name:</span> <span className="text-gray-900">{selectedConfig.details.displayNameTemplate}</span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600">Account Count:</span> <span className="text-gray-900">{selectedConfig.details.accountCount}</span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600">Start Number:</span> <span className="text-gray-900">{selectedConfig.details.startNumber}</span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600">Password Type:</span>{" "}
                                <span className="text-gray-900">
                                    {selectedConfig.details.passwordType === 0
                                        ? "Strong"
                                        : selectedConfig.details.passwordType === 1
                                          ? "Easy to Remember"
                                          : `Prefix + 4 Digits (${selectedConfig.details.passwordPrefix || "Pass"})`}
                                </span>
                            </div>
                            {selectedConfig.details.jobTitle && (
                                <div>
                                    <span className="font-medium text-gray-600">Job Title:</span> <span className="text-gray-900">{selectedConfig.details.jobTitle}</span>
                                </div>
                            )}
                            {selectedConfig.details.department && (
                                <div>
                                    <span className="font-medium text-gray-600">Department:</span> <span className="text-gray-900">{selectedConfig.details.department}</span>
                                </div>
                            )}
                            {selectedConfig.details.usageLocation && (
                                <div>
                                    <span className="font-medium text-gray-600">Usage Location:</span> <span className="text-gray-900">{selectedConfig.details.usageLocation}</span>
                                </div>
                            )}
                            {selectedConfig.details.licenseSkuIds?.length > 0 && (
                                <div className="col-span-full">
                                    <span className="font-medium text-gray-600">Licenses:</span>{" "}
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {selectedConfig.details.licenseSkuIds.map((id) => (
                                            <span key={id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                                {getSkuName(id)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {selectedConfig.details.securityGroupIds?.length > 0 && (
                                <div className="col-span-full">
                                    <span className="font-medium text-gray-600">Security Groups:</span>{" "}
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {selectedConfig.details.securityGroupIds.map((id) => (
                                            <span key={id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                {getGroupName(id)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </details>
            </div>
        );
    };

    // #endregion

    // #region Modals

    const renderModals = () => (
        <>
            {/* Provision Modal */}
            <Modal isOpen={showProvisionModal} onClose={() => setShowProvisionModal(false)} title="Provision Accounts" icon={["fad", "user-plus"]} iconColor="text-blue-600" size="md">
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        This will create <strong>{selectedConfig?.details.accountCount}</strong> accounts in Azure Entra ID starting from number <strong>{selectedConfig?.details.startNumber}</strong>.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                        <FontAwesomeIcon icon={["fad", "info-circle"]} className="mr-1" />
                        Each account will be created with the configured email template, licenses, and security group assignments. Passwords will be generated automatically.
                    </div>
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => setShowProvisionModal(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={provisionAccounts} disabled={actionLoading}>
                        {actionLoading ? (
                            <>
                                <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                                Provisioning...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={["fad", "user-plus"]} className="mr-2" />
                                Provision Accounts
                            </>
                        )}
                    </Button>
                </div>
            </Modal>

            {/* Delete Config Modal */}
            <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Configuration" icon={["fad", "trash"]} iconColor="text-red-600" size="md">
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to delete <strong>{selectedConfig?.name}</strong>?
                    </p>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                            <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-red-600 mt-0.5" />
                            <div className="text-sm text-red-800">
                                <strong>Warning:</strong> This will permanently delete the configuration and all associated account records. Azure AD accounts will NOT be deleted automatically.
                            </div>
                        </div>
                    </div>
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={deleteConfig} disabled={actionLoading}>
                        {actionLoading ? (
                            <>
                                <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={["fad", "trash"]} className="mr-2" />
                                Delete Configuration
                            </>
                        )}
                    </Button>
                </div>
            </Modal>

            {/* Bulk Reset Passwords Modal */}
            <Modal isOpen={showBulkResetModal} onClose={() => setShowBulkResetModal(false)} title="Reset Passwords" icon={["fad", "key"]} iconColor="text-yellow-600" size="md">
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        {selectedAccountIds.size > 0 ? `Reset passwords for ${selectedAccountIds.size} selected account(s)?` : `Reset passwords for all ${accounts.length} accounts?`}
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                            <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-yellow-600 mt-0.5" />
                            <div className="text-sm text-yellow-800">
                                <strong>Note:</strong> New passwords will be generated. You can export the updated credentials after the reset completes.
                            </div>
                        </div>
                    </div>
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => setShowBulkResetModal(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={bulkResetPasswords} disabled={actionLoading}>
                        {actionLoading ? (
                            <>
                                <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                                Resetting...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={["fad", "key"]} className="mr-2" />
                                Reset Passwords
                            </>
                        )}
                    </Button>
                </div>
            </Modal>

            {/* Bulk Enable/Disable Modal */}
            <Modal
                isOpen={showBulkEnableModal}
                onClose={() => setShowBulkEnableModal(false)}
                title={bulkEnableAction ? "Enable Accounts" : "Disable Accounts"}
                icon={["fad", bulkEnableAction ? "check-circle" : "ban"]}
                iconColor={bulkEnableAction ? "text-green-600" : "text-red-600"}
                size="md"
            >
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        {selectedAccountIds.size > 0
                            ? `${bulkEnableAction ? "Enable" : "Disable"} ${selectedAccountIds.size} selected account(s)?`
                            : `${bulkEnableAction ? "Enable" : "Disable"} all ${accounts.length} accounts?`}
                    </p>
                    {!bulkEnableAction && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-red-600 mt-0.5" />
                                <div className="text-sm text-red-800">
                                    <strong>Warning:</strong> Disabling accounts will immediately prevent users from signing in with these credentials.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => setShowBulkEnableModal(false)} disabled={actionLoading}>
                        Cancel
                    </Button>
                    <Button variant={bulkEnableAction ? "success" : "danger"} onClick={bulkEnableDisable} disabled={actionLoading}>
                        {actionLoading ? (
                            <>
                                <FontAwesomeIcon icon={["fad", "spinner"]} className="animate-spin mr-2" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={["fad", bulkEnableAction ? "check-circle" : "ban"]} className="mr-2" />
                                {bulkEnableAction ? "Enable" : "Disable"} Accounts
                            </>
                        )}
                    </Button>
                </div>
            </Modal>

            {/* Export Modal */}
            <Modal isOpen={showExportModal} onClose={() => setShowExportModal(false)} title="Export Accounts" icon={["fad", "file-export"]} iconColor="text-blue-600" size="md">
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Export account credentials for <strong>{selectedConfig?.name}</strong>. The export will include usernames and current passwords.
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <div className="flex items-start gap-2">
                            <FontAwesomeIcon icon={["fad", "lock"]} className="text-yellow-600 mt-0.5" />
                            <div className="text-sm text-yellow-800">
                                <strong>Security Notice:</strong> The exported file will contain sensitive password data. Handle with care and distribute securely.
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <Button variant="primary" onClick={() => exportAccounts("csv")} fullWidth>
                            <FontAwesomeIcon icon={["fad", "file-csv"]} className="mr-2" />
                            Export as CSV
                        </Button>
                        <Button variant="secondary" onClick={() => exportAccounts("json")} fullWidth>
                            <FontAwesomeIcon icon={["fad", "file-code"]} className="mr-2" />
                            Export as JSON
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Password Reset Results Modal */}
            <Modal isOpen={showResetResultModal} onClose={() => setShowResetResultModal(false)} title="Password Reset Results" icon={["fad", "key"]} iconColor="text-green-600" size="lg">
                <div className="p-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2 text-green-800">
                            <FontAwesomeIcon icon={["fad", "check-circle"]} />
                            <span className="font-medium">{resetResults.filter((r) => r.success).length} password(s) reset successfully</span>
                        </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">New Password</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {resetResults.map((result, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-2 text-sm text-gray-900">{result.userPrincipalName}</td>
                                        <td className="px-4 py-2 text-sm font-mono text-gray-900">{result.success ? result.newPassword : "—"}</td>
                                        <td className="px-4 py-2">
                                            {result.success ? (
                                                <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-500" />
                                            ) : (
                                                <span className="text-xs text-red-600">{result.errorMessage}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                        <FontAwesomeIcon icon={["fad", "info-circle"]} className="mr-1" />
                        Copy these passwords now or use the Export function to download them. Passwords are not displayed again after closing this dialog.
                    </p>
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end">
                    <Button variant="secondary" onClick={() => setShowResetResultModal(false)}>
                        Close
                    </Button>
                </div>
            </Modal>
        </>
    );

    // #endregion

    return (
        <div className="space-y-6">
            {/* Action Message */}
            {actionMessage && (
                <div className={`p-4 rounded-lg ${actionMessage.type === "success" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
                    <div className={`flex items-center ${actionMessage.type === "success" ? "text-green-800" : "text-red-800"}`}>
                        <FontAwesomeIcon icon={["fad", actionMessage.type === "success" ? "check-circle" : "exclamation-circle"]} className="w-5 h-5 mr-2" />
                        <span>{actionMessage.text}</span>
                        <button onClick={() => setActionMessage(null)} className="ml-auto text-current opacity-50 hover:opacity-100">
                            <FontAwesomeIcon icon={["fad", "times"]} />
                        </button>
                    </div>
                </div>
            )}

            {view === "list" && renderListView()}
            {(view === "create" || view === "edit") && renderFormView()}
            {view === "detail" && renderDetailView()}

            {renderModals()}
        </div>
    );
};

export default TemporaryAccessIndex;
